import React from 'react';

const Customers: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">CUSTOMERS MANAGEMENT</h1>
          <p className="text-gray-600 mt-2">
              The customer is the party that purchases, consumes, or uses products or services provided by a seller.
          </p>
        </div>

        {/* Main Cutomer Overview */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Supplier Overview</h2>
        <p className="text-gray-600 mb-4">
          The Customer module in DAS allows you to manage all customer information, including contact details,
          payment terms, addresses, and financial balances. This central repository helps streamline procurement processes
          and maintain strong customer relationships.
        </p>

        <div className="mb-6">
          <img
            src="/images/sales/customers/customer.png"
            alt="Suppliers List View"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Key Features</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Complete customer contact management</li>
              <li>Multiple contact persons per supplier</li>
              <li>Billing and shipping address management</li>
              <li>Payment terms and credit limits</li>
              <li>Supplier group categorization</li>
              <li>File attachment support</li>
              <li>Real-time search and filtering</li>
              <li>Financial balance tracking</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Supplier Information</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li><strong>Basic Details:</strong> Name, group, email, phone</li>
              <li><strong>Financial:</strong> Currency, tax settings, payment terms</li>
              <li><strong>Addresses:</strong> Billing and shipping locations</li>
              <li><strong>Contacts:</strong> Multiple contact persons with roles</li>
              <li><strong>Balances:</strong> Payable and advance amounts</li>
              <li><strong>Documents:</strong> Contracts and supporting files</li>
            </ul>
          </div>
        </div>
      </div>


        {/* Customer Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Customer Search and Filtering</h2>
        <p className="text-gray-600 mb-4">
          DAS provides powerful search capabilities to quickly locate customer using both quick search and advanced filtering.
        </p>

        <div className="mb-6">
          <img
            src="/images/sales/customers/customer-search.png"
            alt="Supplier Search Dialog"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Quick Search with Search Icon</h3>
        <p className="text-gray-600 mb-4">
          Use the search icon (🔍) in the top-right corner for instant customer filtering:
        </p>
        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
          <li><strong>Real-time Search:</strong> Filter suppliers as you type</li>
          <li><strong>Multi-field Search:</strong> Searches across names, emails, phones, and groups</li>
          <li><strong>Quick Access:</strong> No separate forms needed</li>
          <li><strong>Immediate Results:</strong> Instant table filtering</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Advanced Search Dialog</h3>
        <p className="text-gray-600 mb-4">
          The advanced search dialog provides detailed filtering options:
        </p>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-gray-600 mb-2"><strong>Search Fields:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Name:</strong> Supplier company name</li>
              <li><strong>Email:</strong> Primary email address</li>
              <li><strong>Phone:</strong> Primary phone number</li>
              <li><strong>Mobile:</strong> Mobile contact number</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Search Benefits:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Precise contact information lookup</li>
              <li>Vendor verification and validation</li>
              <li>Quick procurement partner selection</li>
              <li>Efficient supplier communication</li>
            </ul>
          </div>
        </div>
      </div>




        {/* Add Custom Role Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Creating New Suppliers</h2>
          <p className="text-gray-600 mb-4">
          Add new suppliers to your system with comprehensive information for effective vendor management.
           </p>

            <div className="mb-6">
              <img
                  src="/images/sales/customers/customer-new.png"
                   alt="New Supplier Form"
                   className="w-full border border-gray-300 rounded-lg shadow-sm"
                  />
              </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Basic Information Tab</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Required Fields:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Name:</strong> Company or supplier name (required)</li>
              <li><strong>Customer Group:</strong> Categorization for organization</li>
              <li><strong>Email:</strong> Primary business email</li>
              <li><strong>Phone:</strong> Primary contact number</li>
              <li><strong>Mobile:</strong> Mobile contact number</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Additional Information:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Currency: </strong> Default transaction currency</li>
              <li><strong>Tax:</strong> Tax category and rates</li>
              <li><strong>Opening Balance:</strong> Initial financial balance</li>
              <li><strong>Payment Terms:</strong> Default payment conditions</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Other Details Tab</h3>
        <p className="text-gray-600 mb-4">
          Configure financial and operational settings for the supplier:
        </p>
        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
          <li><strong>Currency Settings:</strong> Define transaction currency preferences</li>
          <li><strong>Tax Configuration:</strong> Set up tax rates and categories</li>
          <li><strong>Opening Balance:</strong> Record initial payable or advance amounts</li>
          <li><strong>Payment Terms:</strong> Configure default payment conditions</li>
          <li><strong>File Attachments:</strong> Upload contracts, certifications, and documents</li>
        </ul>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Best Practice:</strong> Always attach essential documents like business registration,
            tax certificates, and contract agreements to maintain complete supplier records.
          </p>
        </div>
      </div>

        {/* Address Management */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Address Management</h2>
        <p className="text-gray-600 mb-4">
          Manage both billing and shipping addresses for accurate document delivery and communication.
        </p>

        <div className="mb-6">
          <img
            src="/images/sales/customers/customer-address.png"
            alt="Supplier Address Form"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Billing Address</h3>
            <p className="text-gray-600 mb-2">Primary address for invoicing and official communication:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Attention/Contact person</li>
              <li>Country and state selection</li>
              <li>City and detailed address</li>
              <li>Used for purchase orders and invoices</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Shipping Address</h3>
            <p className="text-gray-600 mb-2">Delivery address for goods and services:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Copy from billing address option</li>
              <li>Separate delivery location</li>
              <li>Warehouse or facility address</li>
              <li>Used for delivery coordination</li>
            </ul>
          </div>
        </div>
      </div>


      {/* Contact Persons */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Persons Management</h2>
        <p className="text-gray-600 mb-4">
          Maintain multiple contact persons for different departments and functions within customer organizations.
        </p>

        <div className="mb-6">
          <img
            src="/images/sales/customers/customer-contact-person.png"
            alt="Supplier Contact Persons"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Contact Information Fields</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Personal Details:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Name:</strong> Full contact person name</li>
              <li><strong>Email:</strong> Direct email address</li>
              <li><strong>Phone:</strong> Office phone number</li>
              <li><strong>Mobile:</strong> Personal mobile number</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Role Information:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Designation:</strong> Job title or position</li>
              <li><strong>Department:</strong> Organizational department</li>
              <li><strong>Add New Row:</strong> Multiple contacts support</li>
              <li><strong>Remove:</strong> Delete contact entries</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Notes and Documentation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Notes and Documentation</h2>
        <p className="text-gray-600 mb-4">
          Add important notes and attach relevant documents for comprehensive supplier records.
        </p>

        <div className="mb-6">
          <img
            src="/images/sales/customers/customer-notes.png"
            alt="Supplier Notes"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Notes Section</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Free-text area for important information</li>
              <li>Special instructions or requirements</li>
              <li>Payment preferences and terms</li>
              <li>Quality standards and certifications</li>
              <li>Historical notes and observations</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">File Attachments</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Upload multiple files (max 5MB each)</li>
              <li>Business registration documents</li>
              <li>Tax certificates and licenses</li>
              <li>Contract agreements</li>
              <li>Quality certifications</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Supplier Details View */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Supplier Details and Management</h2>
        <p className="text-gray-600 mb-4">
          View comprehensive customer information and manage customer relationships from the detailed overview.
        </p>

        <div className="mb-6">
          <img
            src="/images/sales/customers/customer-detail.png"
            alt="Supplier Detail View"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Detail View Features</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Supplier Information:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Complete contact details and communication info</li>
              <li>Financial balances and credit information</li>
              <li>Payment terms and transaction currency</li>
              <li>Tax settings and compliance details</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Management Actions:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Edit supplier information</li>
              <li>View financial transaction history</li>
              <li>Access attached documents</li>
              <li>Update contact persons</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Financial Information Display</h3>
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm font-semibold text-gray-700">Payable Balance</p>
            <p className="text-lg text-red-600">Outstanding amounts owed</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm font-semibold text-gray-700">Advance/Credits</p>
            <p className="text-lg text-green-600">Prepaid amounts available</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm font-semibold text-gray-700">Credit Limit</p>
            <p className="text-lg text-blue-600">Maximum credit allowed</p>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Action Buttons and Options</h3>
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Primary Actions:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Edit Button:</strong> Modify supplier information</li>
              <li><strong>More Actions (⋮):</strong> Additional management options</li>
              <li><strong>Print/Export:</strong> Generate supplier reports</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Secondary Actions:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>View Transactions:</strong> Financial history</li>
              <li><strong>Update Status:</strong> Active/inactive management</li>
              <li><strong>Duplicate:</strong> Create similar supplier entries</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Best Practices */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Supplier Management Best Practices</h2>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Data Management</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Keep supplier information updated and accurate</li>
              <li>Regularly verify contact details and addresses</li>
              <li>Maintain comprehensive documentation</li>
              <li>Use supplier groups for better organization</li>
              <li>Set appropriate payment terms and credit limits</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Operational Efficiency</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Utilize search functionality for quick access</li>
              <li>Maintain multiple contact persons for reliability</li>
              <li>Regular review of financial balances</li>
              <li>Proper categorization using supplier groups</li>
              <li>Consistent communication and relationship management</li>
            </ul>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-6">
          <p className="text-sm text-green-800">
            <strong>Success Tip:</strong> Well-maintained supplier records lead to better procurement decisions,
            improved vendor relationships, and streamlined purchasing processes. Regular updates and comprehensive
            documentation are key to effective supplier management.
          </p>
        </div>



      </div>
      </div>
    </div>
  );
};

export default Customers;
